import { useState } from "react";
import VehicleChildForm from "./components/vehicleChildForm";
import { requiredFieldsByStep } from "./utils/required-fields-by-step";
import DealershipChildForm from "./components/dealershipChildForm";
import ServiceChildForm from "./components/serviceChildForm";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import { IForm } from "../../";
import { fieldsLabels } from "./utils/fields-labels";
import { Occurrence } from "../../../../models/occurrence";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
} from "../../../../enums/occurrenceStep";
import { FinalizeOccurrenceButton } from "../../dialogs/finalizationReasonDialog";

const CustomerOkForm = (props: IForm) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasAdvanceError, setHasAdvanceError] = useState(false);

  const requiredServiceFields = (props.occurrence?.occurrenceType === "P"
    ? requiredFieldsByStep.CUSTOMER_OK_PREVENTIVE_CHECKUP
    : requiredFieldsByStep.CUSTOMER_OK
  ).filter(f => props.occurrence?.occurrenceType !== 'D' || !['vehicleStopped', 'hasLink', 'mainOccurrence', 'tasNumber', 'tasStatus'].includes(f));

  const isDealership = props.occurrence?.occurrenceType === 'D';
  const partOrderRequiredFields = isDealership ? [] : ['partOrders', 'partOrderParts'];
  const serviceHiddenFields = isDealership
    ? ['estimatedTime','partOrderStatus','osNumber','osDtOpenAt','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','tasNumber','tasStatus','checklist','solutionProposed','criticality','createdBy','dtStart','dtEnd','modules','estimateTimeRepair','visualInspection','vehicleStopped','hasLink','mainOccurrence','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload']
    : ['timeOpenProtocol','country','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload','partOrderStatus','checklist','solutionProposed'];

  return (
    <div>
      <div className="text-size-h4 font-bold">Ok do Cliente</div>
      <div className="text-color-status-resting text-size-14">
        Atribua os dados para relacionar a etapa de ok do cliente
      </div>

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId"]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "dealershipId",
          "dealershipUniqueId",
          "dealershipLocation",
          "dealershipRepresentative",
          "dealershipRegional",
          "dealershipPhone",
          "dealershipDemarcatedArea",
        ]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={serviceHiddenFields}
        disabledFields={[
          "estimateTimeRepair",
          "criticality",
          "createdBy",
          "occurrenceType",
          "source",
          "modules",
          "hasLink",
          "mainOccurrence",
          "literatureTroubleshooting",
          "tasNumber",
          "tasStatus",
          "serviceBaySchedule",
        ]}
        disabledAllFields={props.disabledAllFields}
        requiredFields={requiredServiceFields}
        step={props.step}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        hasError={hasError}
        persona={props.persona}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        !props.hiddenSaveButton && (
          <div className="flex justify-end">
            <button
              className={`btn btn-primary ${
                props.loading ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence) return;
                setHasError(!props.isValidStep(requiredServiceFields, props.occurrence));
                const success = await props.save(props.occurrence);
                if (success) {
                  setShowSuccessDialog(true);
                } else {
                  setShowErrorDialog(true);
                }
              }}
              disabled={props.loading || props.disabledAllFields}
            >
              Salvar
            </button>

            <FinalizeOccurrenceButton
              occurrence={props.occurrence}
              finishOccurrence={props.finishOccurrence}
              disabledAllFields={props.disabledAllFields}
              persona={props.persona}
              stepId={props.step?.id}
            />
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Erro ao salvar"
        body="Ocorreu um erro ao salvar a etapa de ok do cliente. Por favor, tente novamente ou entre em contato com o suporte."
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Ok do cliente salvo com sucesso"
        body={
          <div className="space-y-4">
            <div>O ok do cliente foi salvo com sucesso.</div>
            {!hasAdvanceError && (
              <div>
                Deseja avançar para a etapa {OccurrenceStepLabelEnum.PARTS}?{" "}
              </div>
            )}
            {hasAdvanceError && (
              <>
                <div>
                  É necessário preencher todos os campos obrigatórios para
                  avançar/retroceder de etapa.
                </div>
                <div>Campos obrigatórios não preenchidos:</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      [...requiredServiceFields, ...partOrderRequiredFields],
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {fieldsLabels[field as keyof typeof fieldsLabels]}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
        footer={
          <button
            className="btn btn-primary"
            disabled={hasAdvanceError || !props.changeStep || !props.occurrence}
            onClick={() => {
              const advanceError = !props.isValidStep([...requiredServiceFields, ...partOrderRequiredFields], props.occurrence ?? ({} as Occurrence));
              setHasAdvanceError(advanceError);
              
              if (advanceError) {
                return;
              }

              props.summaryStep?.id && props.changeStep!(
                props.occurrence!,
                props.summaryStep.id,
                OccurrenceStepEnum.PARTS
              );
              setShowSuccessDialog(false);
              props.onClose();
            }}
          >
            Avançar etapa
          </button>
        }
      />
    </div>
  );
};

export default CustomerOkForm;
